import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { apiUrl } from "@/lib/api";
import MfaCodeForm from "../components/MfaCodeForm";
import { needsMfa, takePendingMfa } from "../utils/mfa";
import { facebookStartUrl } from "@/lib/oauthNonce";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react";
import { GOOGLE_CLIENT_ID, microsoftLoginRequest, FACEBOOK_LOGIN_ENABLED } from "@/config/oauth";
import { isInAppBrowser } from "@/utils/browserEnv";
import { isAppleSignInAvailable, signInWithApple } from "@/utils/appleSignIn";
import { storeAuthTokens, resolvePostLoginPath, safeNextPath } from "@/utils/postAuth";
import { trackEvent } from "@/lib/tracker";
import { useBranding } from "../contexts/BrandingContext";
import { SiftMark } from "../components/SiftWordmark";

function AppleButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full min-h-[44px] h-12 flex items-center justify-center gap-3 rounded-pill font-medium text-white bg-black hover:bg-gray-900 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <svg width="18" height="18" viewBox="0 0 384 512" fill="white" aria-hidden="true">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C53.2 141.6 0 184.5 0 270.5c0 25.4 4.7 51.6 14 78.5 12.6 35.5 58 122.6 105.4 121.1 24.8-.6 42.3-17.6 74.5-17.6 31.3 0 47.5 17.6 76.4 17.6 47.8-.7 88.8-79.8 100.8-115.4-64.1-30.2-60.8-88.5-60.8-90zM256.3 91.2c30.2-35.8 27.4-68.4 26.5-80.2-26.6 1.5-57.4 18.1-74.9 38.6-19.3 22-30.6 49.3-28.2 80.1 28.8 2.2 55.1-12.6 76.6-38.5z"/>
      </svg>
      Sign in with Apple
    </button>
  );
}

function GoogleButton({ onSuccess, disabled }) {
  const login = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => {
      onSuccess({ access_token: tokenResponse.access_token });
    },
    onError: () => toast.error("Google sign-in failed."),
  });

  return (
    <button
      type="button"
      onClick={() => login()}
      disabled={disabled}
      className="w-full min-h-[44px] h-12 flex items-center justify-center gap-3 border border-line-strong rounded-pill font-medium text-ink bg-surface hover:border-brand hover:text-brand active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <svg width="20" height="20" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      </svg>
      Sign in with Google
    </button>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Why the last password sign-in failed, shown by the button and kept until
  // they edit a field. A toast vanished in four seconds and was the only
  // place it said so (WCAG 3.3.1).
  const [loginError, setLoginError] = useState("");
  const [oauthLoading, setOauthLoading] = useState(false);
  // Set when the password (or a social sign-in) was right and the account has
  // two-step sign-in on: the page asks for the code instead of signing in.
  const [mfaToken, setMfaToken] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // A sign-in that started elsewhere (signup, landing pages) and needs a code.
    const pending = takePendingMfa();
    if (pending) setMfaToken(pending);
    // Facebook signs in by redirect and comes back here when it can't finish.
    const reason = new URLSearchParams(window.location.search).get("social_error");
    if (reason) {
      setLoginError(reason === "mfa_required"
        ? "Your account has two-step sign-in on. Sign in with your email and password, then enter your code. No password yet? Use Forgot password to set one."
        : "That sign-in didn't finish. Try again, or sign in with your email and password.");
    }
  }, []);
  const { instance: msalInstance } = useMsal();
  const branding = useBranding();

  // Post-login routing now lives in @/utils/postAuth so email login, social
  // login, and landing-page social signup all route identically.
  // `data.user` is whatever the auth response already told us about this
  // person. Handing it straight to the resolver saves the /me/ call it would
  // otherwise make purely to read their org role. Social responses that omit
  // a user object pass undefined, and the resolver falls back to fetching.
  const getPostLoginPath = (user) => resolvePostLoginPath(branding, user);

  // Shared: store token and redirect
  const handleAuthSuccess = async (data) => {
    if (needsMfa(data)) {
      setMfaToken(data.mfa_token);
      return;
    }
    storeAuthTokens(data);
    toast.success("Login successful!");
    // If we just came through Register with a ?next= hint, send the user
    // there instead of the default dashboard. The sessionStorage handoff
    // is set by RegisterPage so it survives the verify-email round-trip.
    let dest = null;
    try {
      const stashed = safeNextPath(sessionStorage.getItem("post_register_next"));
      if (stashed) {
        dest = stashed;
        sessionStorage.removeItem("post_register_next");
      }
    } catch { /* ignore storage failures */ }
    // A ?next= on the sign-in link itself (a post's "Sign in to send" link,
    // for one). It was never read, so those links dropped people on their
    // dashboard instead of back where they were (2026-11-01).
    if (!dest) {
      try { dest = safeNextPath(new URLSearchParams(window.location.search).get("next")); } catch { /* no URL */ }
    }
    if (!dest) dest = await getPostLoginPath(data?.user);
    navigate(dest);
  };

  // Email/password login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError("");

    /* How long signing in actually takes, from their side of it.
       Server-side numbers miss the two things that decide whether this feels
       fast: the network under a real phone, and the CORS preflight the browser
       insists on before the POST. Only the password path is timed -- a social
       sign-in spends most of its wall clock inside the provider's own UI, which
       is not ours to measure or to fix, and averaging the two together would
       hide whichever one regressed. */
    const startedAt = (typeof performance !== "undefined" ? performance.now() : Date.now());

    try {
      const res = await fetch(apiUrl("/api/accounts/login/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "omit",
        mode: "cors",
      });

      if (!res.ok) {
        let errorMsg = "Incorrect email or password.";
        try {
          const errorData = await res.json();
          const said = errorData.detail || errorData.error || errorData.message;
          // Only a sentence reaches the screen, never a raw object or list.
          if (typeof said === "string" && said.trim()) errorMsg = said;
          else if (Array.isArray(said) && typeof said[0] === "string") errorMsg = said[0];
        } catch {
          /* not JSON: keep the default sentence */
        }
        throw new Error(errorMsg);
      }

      const data = await res.json();
      trackEvent("login_completed", {
        method: "password",
        duration_ms: Math.round(
          (typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt
        ),
      });
      handleAuthSuccess(data);
    } catch (error) {
      if (error.message === "Failed to fetch") {
        setLoginError("Cannot connect to server. Please check your internet connection.");
      } else {
        setLoginError(error?.message || "Login failed. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth
  const handleGoogleSuccess = async (tokenResponse) => {
    setOauthLoading(true);
    try {
      const res = await fetch(apiUrl("/api/accounts/oauth/google/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ access_token: tokenResponse.access_token }),
        credentials: "omit",
        mode: "cors",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Google login failed.");
      }
      handleAuthSuccess(await res.json());
    } catch (error) {
      toast.error(error?.message || "Google login failed.");
    } finally {
      setOauthLoading(false);
    }
  };

  // Apple OAuth (Sign in with Apple)
  const handleAppleLogin = async () => {
    setOauthLoading(true);
    try {
      const { identity_token, first_name, last_name } = await signInWithApple();
      const res = await fetch(apiUrl("/api/accounts/oauth/apple/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ identity_token, first_name, last_name }),
        credentials: "omit",
        mode: "cors",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Apple login failed.");
      }
      handleAuthSuccess(await res.json());
    } catch (error) {
      // User-cancelled native sheet: stay quiet.
      const msg = String(error?.message || "");
      if (/cancel/i.test(msg) || error?.code === "1001") return;
      toast.error(msg || "Apple login failed.");
    } finally {
      setOauthLoading(false);
    }
  };

  // Microsoft OAuth
  const handleMicrosoftLogin = async () => {
    setOauthLoading(true);
    try {
      const loginResponse = await msalInstance.loginPopup(microsoftLoginRequest);
      const res = await fetch(apiUrl("/api/accounts/oauth/microsoft/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ access_token: loginResponse.accessToken, id_token: loginResponse.idToken }),
        credentials: "omit",
        mode: "cors",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Microsoft login failed.");
      }
      handleAuthSuccess(await res.json());
    } catch (error) {
      if (error?.errorCode === "user_cancelled") return;
      toast.error(error?.message || "Microsoft login failed.");
    } finally {
      setOauthLoading(false);
    }
  };

  const anyLoading = loading || oauthLoading;

  /* Facebook, by redirect rather than popup — which is why it is the one social
     button that survives an embedded browser, and why it can be offered here
     while Google and Microsoft cannot. Same endpoint signup uses: the backend
     logs an existing user in and creates one otherwise, so it serves both. */
  const inAppBrowser = isInAppBrowser();

  const startFacebook = () => {
    window.location.href = facebookStartUrl(window.location.pathname + window.location.search);
  };

  return (
    <>
      {/* Header */}
      <div className="text-center space-y-2 mb-8">
        {branding.branded && branding.brand_logo_url ? (
          <img src={branding.brand_logo_url} alt={branding.brand_name} className="h-10 mx-auto mb-3 object-contain" />
        ) : !branding.branded ? (
          <div className="flex justify-center mb-3"><SiftMark size={52} /></div>
        ) : null}
        <h2 className="text-3xl sm:text-4xl font-display italic tracking-tight text-ink">
          {branding.branded && branding.brand_logo_url
            ? "Welcome Back"
            : branding.branded
              ? `Welcome to ${branding.brand_name}`
              : "Welcome Back"}
        </h2>
        <p className="text-sm sm:text-base text-faint">
          {branding.branded
            ? `Sign in to access your ${branding.brand_name} portal`
            : "Sign in to your Sift Careers account"}
        </p>
      </div>

      {mfaToken ? (
        <MfaCodeForm
          mfaToken={mfaToken}
          onVerified={handleAuthSuccess}
          onCancel={() => { setMfaToken(null); setPassword(""); }}
          buttonClass={branding.branded ? "" : "bg-brand hover:bg-brand-hover"}
          buttonStyle={branding.branded ? { backgroundColor: branding.primary_color } : undefined}
        />
      ) : (
      <>
      {/* OAuth Buttons

          Facebook is first and is not optional here. Every OAuth account is
          created with set_unusable_password() (accounts/oauth_views.py:39), so
          somebody who signed up with Facebook has no password to fall back on.
          Signup has offered Facebook since the button shipped; login did not,
          which locked those accounts out of the product entirely. Same
          redirect endpoint as signup — the backend logs in an existing user and
          creates one otherwise, so a single button serves both.

          Google and Microsoft hide inside Facebook's in-app browser, matching
          what SocialAuthButtons already does on every signup surface. This page
          predates that gate and never got it, so it has been rendering
          popup-based buttons into the one browser where popups do not work —
          for the majority of our traffic, since most of it arrives from
          Facebook. Facebook and Apple stay: Facebook is a redirect, and Apple
          only renders on native iOS. */}
      <div className="space-y-3 mb-6">
        {FACEBOOK_LOGIN_ENABLED && (
          <button
            type="button"
            onClick={startFacebook}
            disabled={anyLoading}
            className="w-full min-h-[44px] h-12 flex items-center justify-center gap-3 border border-line-strong rounded-pill font-medium text-ink bg-surface hover:border-brand hover:text-brand active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="#1877F2"
                d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.96h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"
              />
            </svg>
            Sign in with Facebook
          </button>
        )}

        {isAppleSignInAvailable() && (
          <AppleButton onClick={handleAppleLogin} disabled={anyLoading} />
        )}

        {!inAppBrowser && GOOGLE_CLIENT_ID && (
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <GoogleButton onSuccess={handleGoogleSuccess} disabled={anyLoading} />
          </GoogleOAuthProvider>
        )}

        {!inAppBrowser && (
          <button
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={anyLoading}
            className="w-full min-h-[44px] h-12 flex items-center justify-center gap-3 border border-line-strong rounded-pill font-medium text-ink bg-surface hover:border-brand hover:text-brand active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg width="20" height="20" viewBox="0 0 21 21">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </button>
        )}
      </div>

      {/* Divider */}
      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-line" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-surface px-4 text-tertiary">or continue with email</span>
        </div>
      </div>

      {/* Login Form */}
      <form onSubmit={handleLogin} className="space-y-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-secondary mb-2">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => { setEmail(e.target.value); setLoginError(""); }}
            aria-invalid={!!loginError}
            aria-describedby={loginError ? "login-error" : undefined}
            disabled={anyLoading}
            placeholder="your@email.com"
            className="w-full min-h-[44px] h-12 px-4 bg-surface text-ink placeholder-faint border border-line-control rounded-lg [color-scheme:light] focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="password" className="block text-sm font-medium text-secondary">
              Password
            </label>
            <Link
              to="/forgot-password"
              className="text-sm text-brand hover:text-brand-hover no-underline transition-colors"
            >
              Forgot password?
            </Link>
          </div>
          <input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => { setPassword(e.target.value); setLoginError(""); }}
            aria-invalid={!!loginError}
            aria-describedby={loginError ? "login-error" : undefined}
            disabled={anyLoading}
            placeholder="••••••••"
            className="w-full min-h-[44px] h-12 px-4 bg-surface text-ink placeholder-faint border border-line-control rounded-lg [color-scheme:light] focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {loginError && (
          <p id="login-error" role="alert" className="text-sm font-medium text-red-700">
            {loginError}
          </p>
        )}

        <button
          type="submit"
          disabled={anyLoading}
          className={`w-full min-h-[44px] h-12 text-white rounded-pill font-medium active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 ${
            branding.branded ? "" : "bg-brand hover:bg-brand-hover"
          }`}
          style={branding.branded
            ? { backgroundColor: branding.primary_color }
            : undefined}
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
      </>
      )}

      {/* Register Link */}
      <div className="mt-8 text-center text-sm text-faint">
        Don't have an account?{" "}
        <Link
          to="/auth/register"
          className={`font-medium no-underline transition-colors ${
            branding.branded ? "hover:underline" : "text-brand hover:text-brand-hover"
          }`}
          style={branding.branded ? { color: branding.primary_color } : undefined}
        >
          Create account
        </Link>
      </div>

      {/* Additional Links */}
      <div className="mt-6 pt-6 border-t border-line flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-sm text-faint">
        {!branding.branded && (
          <>
            {/* The doors, not the bare form. /auth/organization/register is a
                registration screen and nothing else: a company arriving from
                here has said only that they are a company, so the next screen
                asks whether they want to write a post or just open an account.
                The bare form still exists and is still where a partner code
                redirect lands; it is just no longer what a cold visitor meets. */}
            <Link
              to="/get-started?a=business&c=1"
              className="text-brand hover:text-brand-hover no-underline transition-colors"
            >
              Register as Company
            </Link>
            <span className="hidden sm:inline text-tertiary">&bull;</span>
          </>
        )}
        <Link to="/" className="text-brand hover:text-brand-hover no-underline transition-colors">
          Back to Home
        </Link>
      </div>
      {branding.branded && (
        <div className="mt-4 text-center text-xs text-gray-400">
          Powered by{" "}
          <a href="https://siftcareers.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
            Sift Careers
          </a>
        </div>
      )}
    </>
  );
}
